<template>
  <scroll-view
    class="custom-virtual-list"
    :scroll-x="scrollX"
    :scroll-y="scrollY"
    :upper-threshold="upperThreshold"
    :lower-threshold="lowerThreshold"
    :scroll-top="scrollTop"
    :scroll-left="scrollLeft"
    :scroll-into-view="scrollIntoView"
    :scroll-with-animation="scrollWithAnimation"
    :enable-back-to-top="enableBackToTop"
    :show-scrollbar="showScrollbar"
    :refresher-enabled="refresherEnabled"
    :refresher-threshold="refresherThreshold"
    :refresher-default-style="refresherDefaultStyle"
    :refresher-background="refresherBackground"
    :refresher-triggered="refresherTriggered"
    @scroll="handleScroll"
    @scrolltoupper="handleScrollToUpper"
    @scrolltolower="handleScrollToLower"
    @refresherpulling="handleRefresherPulling"
    @refresherrefresh="handleRefresherRefresh"
    @refresherrestore="handleRefresherRestore"
    @refresherabort="handleRefresherAbort"
	
  >
    <!-- 占位容器，撑开滚动区域 -->
    <view class="virtual-list-placeholder" :style="{ 
		width : scrollX ? totalHeight + 'px' : '100%',
		height: scrollY ? totalHeight + 'px' : '100%',
	}"></view>
    
    <!-- 实际渲染的列表项 -->
    <view class="virtual-list-content" :style="{
		display: scrollX ? 'flex' : 'block',
		transform: scrollX ? `translateX(${offsetX}px)` : `translateY(${offsetY}px)` 
	}">
      <view 
        v-for="(item, index) in visibleData"
        :key="dataKey ? item[dataKey] : index"
        class="virtual-list-item"
        :style="{ height: itemHeight + 'px' }"
		@mousedown.stop.prevent="dragStart"
      >
        <slot :item="item" :index="startIndex + index"></slot>
      </view>
    </view>
  </scroll-view>
</template>

<script>
export default {
  name: 'CustomVirtualList',
  props: {
    // 虚拟列表特有属性
    data: {
      type: Array,
      default: () => []
    },
    itemHeight: {
      type: Number,
      required: true
    },
    bufferSize: {
      type: Number,
      default: 5
    },
    dataKey: {
      type: String,
      default: ''
    },
    sort:{
	  type: Boolean,
	  default:true,
	},
	
    // scroll-view原有属性
    scrollX: {
      type: Boolean,
      default: false
    },
    scrollY: {
      type: Boolean,
      default: true
    },
    upperThreshold: {
      type: [Number, String],
      default: 50
    },
    lowerThreshold: {
      type: [Number, String],
      default: 50
    },
    scrollTop: {
      type: [Number, String],
      default: 0
    },
    scrollLeft: {
      type: [Number, String],
      default: 0
    },
    scrollIntoView: {
      type: String,
      default: ''
    },
    scrollWithAnimation: {
      type: Boolean,
      default: false
    },
    enableBackToTop: {
      type: Boolean,
      default: false
    },
    showScrollbar: {
      type: Boolean,
      default: true
    },
    refresherEnabled: {
      type: Boolean,
      default: false
    },
    refresherThreshold: {
      type: Number,
      default: 45
    },
    refresherDefaultStyle: {
      type: String,
      default: 'black'
    },
    refresherBackground: {
      type: String,
      default: '#FFFFFF'
    },
    refresherTriggered: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      startIndex: 0,
      endIndex: 0,
      offsetX: 0,
	  offsetY: 0,
      visibleData: [],
      containerHeight: 0,
	  containerWidth: 0,
    }
  },
  computed: {
    totalHeight() {
      return this.data.length * this.itemHeight;
    }
  },
  mounted() {
    this.initVirtualList();
    // 获取容器大小
    this.getContainerSize();
  },
  watch: {
    data() {
		this.updateVisibleData(this.scrollTop);
    },
    scrollTop(newVal) {
      this.handleScrollTopChange(newVal);
    },
	scrollLeft(newVal) {
	  this.handleScrollLeftChange(newVal);
	}
  },
  methods: {
    initVirtualList() {
      this.updateVisibleData();
    },
    
    getContainerSize() {
      const query = uni.createSelectorQuery().in(this);
      query.select('.custom-virtual-list').boundingClientRect(data => {
        if (data) {
          this.containerHeight = data.height;
		  this.containerWidth = data.width;
          this.updateVisibleData();
        }
      }).exec();
    },
    
    handleScroll(e) {
		const sp = this.scrollX ? e.detail.scrollLeft : e.detail.scrollTop;
		this.updateVisibleData(sp);
		this.$emit('scroll', e);
    },
    
    handleScrollTopChange(sp) {
      this.updateVisibleData(Number(sp));
    },
	
	handleScrollLeftChange(sp) {
	  this.updateVisibleData(Number(sp));
	},
    
    updateVisibleData(sp = 0) {
      // 计算可视区域的起始和结束索引
      const startIndex = Math.floor(sp / this.itemHeight);
      const visibleCount = this.scrollX ? 
		Math.ceil(this.containerWidth / this.itemHeight)
		:
		Math.ceil(this.containerHeight / this.itemHeight);
		
      const endIndex = Math.min(startIndex + visibleCount + this.bufferSize, this.data.length);
      
      // 更新偏移量和可视数据
      this.startIndex = Math.max(0, startIndex - this.bufferSize);
      this.endIndex = endIndex;
	  
	  if(this.scrollX){
		  this.offsetX = this.startIndex * this.itemHeight;
	  } else {
		  this.offsetY = this.startIndex * this.itemHeight;
	  }
      this.visibleData = this.data.slice(this.startIndex, this.endIndex);
      
      // 触发可视区域变化事件
      this.$emit('visible-change', {
        startIndex: this.startIndex,
        endIndex: this.endIndex,
        visibleData: this.visibleData
      });
    },
    
    // 转发scroll-view的事件
    handleScrollToUpper(e) {
      this.$emit('scrolltoupper', e);
    },
    
    handleScrollToLower(e) {
      this.$emit('scrolltolower', e);
    },
    
    handleRefresherPulling(e) {
      this.$emit('refresherpulling', e);
    },
    
    handleRefresherRefresh(e) {
      this.$emit('refresherrefresh', e);
    },
    
    handleRefresherRestore(e) {
      this.$emit('refresherrestore', e);
    },
    
    handleRefresherAbort(e) {
      this.$emit('refresherabort', e);
    },
	scrollTo(id){
		let idx = 0;
		
		if(id) idx = this.data.findIndex(v=>v.id==id);

		if(idx>-1){
			this.handleScroll({ detail:{ 
				scrollTop	: this.scrollX ? 0 : idx * this.itemHeight,
				scrollLeft	: this.scrollY ? 0 : idx * this.itemHeight,
			} })
		}
	},
	
	dragStart(e){
		// if(!this.sort || !this.data || this.data.length <=0) return;
		// this.elem	= this.getParent(e.target);
		// if(!this.elem) return;
		// this.index	= this.getIndex(this.elem);
		// this.start	= this.portrait ? e.screenY : e.screenX;
		// this.time	= new Date().getTime();
		// console.log('dragStart')
	},
	dragMove(e){;
		if(!this.sort || !this.data || this.data.length <=0 || !this.elem) return;
		
		this.$nextTick(()=>{
			if(e.buttons == 1 && this.start >0){
				let elem	= this.getParent(e.target);
				if(!elem) return;
				
				let p		= this.portrait ? e.screenY : e.screenX;
				let index	= this.getIndex(elem);
				let n		= p - this.start;
			
				//console.log(elem, this.elem);
				if(elem == this.elem) return;
				
				if(n < 0 && index > this.index) return;
				if(n > 0 && index < this.index) return;
				
				if(index != this.index){
					if(index<0) index = 0;
					
					this.$emit('change', {
						before  : this.index,
						after	: index,
					});
		
					let item = this.data[this.index];
					
					if(!elem.classList['dy-sort-move']){
						elem.classList.add('dy-sort-move');
						setTimeout(()=>{
							elem.classList.remove('dy-sort-move');
						}, 100);
					}
				
					// console.log(this.data);
					this.data.splice(this.index, 1);
					this.data.splice(index, 0, item);
					
					this.index	= index;
					this.elem	= elem;
					this.start = 0;
				}
				
				this.start = p;
			}
		});
	},
	dragEnd(e){
		this.start = 0;
	}
  }
}
</script>

<style scoped>
.custom-virtual-list {
  position: relative;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  margin-right: -8px;
}
.virtual-list-placeholder {
	width: 100%;
}

.virtual-list-content {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	z-index: 1;
}

.virtual-list-item {
	width: 100%;
	box-sizing: border-box;
	flex:1;
	flex-grow: 0;
}
</style>
